import { TPortfolioStatus } from '../api/portfolio/types'

/**
 * The order the leases list opens in: the statuses an agent acts on first, then the ones that
 * are only history. Doubles as the sort rank for the Status column, so the column and the
 * default sort can't disagree.
 */
export const LEASE_STATUS_ORDER: TPortfolioStatus[] = [
  'draft',
  'active',
  'expiring',
  'renewal',
  'expired',
  'terminated',
  'deleted',
]

const STATUS_LABELS: Record<TPortfolioStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  expiring: 'Expiring',
  renewal: 'Renewal',
  expired: 'Expired',
  terminated: 'Terminated',
  deleted: 'Archived',
}

/**
 * The palette the status badges have always used. Kept here rather than in the badge so the
 * list's status markers read the same colours.
 */
const STATUS_COLORS: Record<TPortfolioStatus, string> = {
  renewal: '#00B663',
  expired: '#b10012',
  terminated: '#b10012',
  deleted: '#b10012',
  expiring: '#eb0018',
  active: '#35383b',
  draft: '#c62482',
}

/**
 * The status badges have always rendered their colour at 64%, so the markers do too — a dot and
 * the badge for the same lease read as one colour rather than two shades of it.
 */
export const LEASE_STATUS_COLOR_OPACITY = 0.64

const asStatus = (status?: string): TPortfolioStatus => {
  const normalised = (status ?? '').toLowerCase() as TPortfolioStatus
  return normalised in STATUS_LABELS ? normalised : 'draft'
}

export const getLeaseStatusLabel = (status?: string): string => STATUS_LABELS[asStatus(status)]

export const getLeaseStatusColor = (status?: string): string => STATUS_COLORS[asStatus(status)]

/**
 * Sortable rank for `meta.status`. Padded so it compares as a string alongside the table's
 * other meta values, and so a tenth status wouldn't sort between the first and the second.
 */
export const getLeaseStatusRank = (status?: string): string =>
  String(LEASE_STATUS_ORDER.indexOf(asStatus(status))).padStart(2, '0')
